import React from "react"

import { format } from "date-fns"
import SignaturePad from "react-signature-canvas"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { replaceContractPlaceholders } from "@/utils/pdfUtils"

interface ContractReviewFormProps {
  onSign: (signature: string | null) => void
  signature: string | null
}

const ContractReviewForm: React.FC<ContractReviewFormProps> = ({ onSign, signature }) => {
  const { job } = useActiveJob()
  const { application } = useApplication()
  const signaturePad = React.useRef(null)

  const clear = () => {
    if (signaturePad.current) {
      signaturePad.current.clear()
      onSign(null)
    }
  }

  const save = () => {
    if (signaturePad.current && !signaturePad.current.isEmpty()) {
      const signatureData = signaturePad.current.toDataURL()
      onSign(signatureData)
    }
  }

  const address = application?.personalDetails?.address?.line1
    ? application?.personalDetails?.address?.line1 +
      ", " +
      application?.personalDetails?.address?.city +
      ", " +
      application?.personalDetails?.address?.postcode
    : ""
  const fullName = application?.basicDetails?.firstName + " " + application?.basicDetails?.lastName
  const driverNumber = application?.airtablePersonId
  const companyName = application?.personalDetails?.company?.number || fullName
  const vatNumber = application?.personalDetails?.company?.vatNumber
  const currentStage = job.stages.find((stage) => stage.id === "contract")
  const contractText = currentStage?.meta?.contract as string
  const defaultSalary = currentStage?.meta?.defaultSalary as string
  const contract = replaceContractPlaceholders(contractText || "", {
    "full-name": fullName,
    "driver-number": driverNumber,
    "company-name-or-full-name": companyName,
    address: address,
    "vat-number": vatNumber,
    salary: application?.contractSalary || defaultSalary,
    date: format(new Date(), "MMMM d, yyyy"),
  })

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="border-b bg-gray-50 p-6">
          <h3 className="text-xl font-semibold text-jungle">Delivery Driver Contract</h3>
          <p className="mt-1 text-sm text-gray-600">
            Please read the contract carefully before signing
          </p>
        </div>

        <div className="h-96 space-y-4 overflow-y-auto p-6 text-base leading-relaxed">
          {contract ? (
            <div
              className="contract-content prose w-full max-w-full whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: contract }}
            />
          ) : (
            <div className="flex h-64 items-center justify-center">
              <p className="text-gray-500">Contract not available</p>
            </div>
          )}
        </div>
        <div className="border-t bg-gray-50 p-4">
          <p className="font-medium text-gray-700">
            By signing below, the Driver acknowledges that they have read, understood, and agree to
            all terms and conditions of this Agreement.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-lg font-medium">Your Signature</Label>
        {signature ? (
          <div className="space-y-3">
            <div className="rounded-md border bg-white p-4 shadow-sm">
              <img src={signature} alt="Signature" className="max-h-40 w-full object-contain" />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSign(null)}
              className="w-full md:w-auto"
            >
              Clear Signature
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="touch-none rounded-md border bg-white shadow-sm">
              <SignaturePad
                ref={signaturePad}
                canvasProps={{
                  className: "w-full h-40 touch-none",
                }}
              />
            </div>
            <div className="flex flex-col gap-2 md:flex-row">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clear}
                className="w-full md:w-auto"
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={save}
                className="w-full bg-honeycomb hover:bg-amber-500 md:w-auto"
              >
                Sign Contract
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContractReviewForm
